import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const PORTUGUESE_LOCALES = ['pt-br', 'pt-pt'];

const faqItems = [
  {
    q: 'Como funciona a calculadora de dívidas?',
    a: 'Insira cada dívida com o saldo atual, taxa de juros anual e pagamento mínimo mensal. A calculadora aplica o método bola de neve ou avalanche para mostrar sua data exata de quitação de dívidas, o total de juros que você pagará e quanto economiza pagando um pouco extra por mês.',
  },
  {
    q: 'Qual é melhor: método bola de neve ou avalanche?',
    a: 'Depende do seu perfil. O método avalanche (pagar primeiro a dívida com maior taxa de juros) economiza mais dinheiro no total. O método bola de neve (pagar primeiro a menor dívida) traz vitórias rápidas que mantêm a motivação. Ambos são muito mais eficazes do que pagar apenas o mínimo.',
  },
  {
    q: 'Quanto tempo leva para pagar minhas dívidas?',
    a: 'Depende do saldo, da taxa de juros e de quanto você paga por mês. Pagando apenas o mínimo, uma dívida de cartão de crédito pode demorar mais de 10 anos. Adicionando um valor extra ao pagamento mensal, você pode quitar em 2–3 anos e economizar milhares em juros. Use o simulador de quitação de dívidas para ver seu plano personalizado.',
  },
  {
    q: 'Esta calculadora é gratuita?',
    a: 'A calculadora de dívidas é completamente gratuita e não requer cadastro. Você pode calcular seu plano para pagar dívidas em segundos. Se quiser salvar e baixar seu plano personalizado em PDF, existe uma opção de pagamento único sem assinatura.',
  },
  {
    q: 'Funciona para cartão de crédito e empréstimos?',
    a: 'Sim. Esta calculadora de dívidas funciona para cartões de crédito, empréstimos pessoais, financiamentos de veículos, crédito estudantil e qualquer tipo de dívida com juros. Você pode inserir várias dívidas de uma vez para calcular o tempo total para ficar livre de dívidas.',
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'Como usar este simulador de quitação de dívidas',
    body: 'Insira cada dívida: saldo atual, taxa de juros anual e pagamento mínimo mensal. Adicione qualquer valor extra que você possa destinar ao pagamento. A calculadora de dívidas mostra sua data de quitação, o total de juros e como cada estratégia — bola de neve ou avalanche — acelera como sair das dívidas.',
  },
  {
    heading: 'Método bola de neve vs avalanche: qual escolher?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          O <strong style={{ color: COLORS.text }}>método bola de neve de dívidas</strong> consiste em
          pagar primeiro a menor dívida e usar esse pagamento liberado para atacar a próxima.
          É ideal se você precisa de motivação e vitórias rápidas no seu plano para pagar dívidas.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          O <strong style={{ color: COLORS.text }}>método avalanche</strong> ataca primeiro a dívida com
          maior taxa de juros. Matematicamente é mais eficiente e reduz mais o custo total das
          dívidas de cartão de crédito e empréstimos. Use nosso simulador de quitação de dívidas
          para calcular quanto você economiza com cada método.
        </p>
      </>
    ),
  },
  {
    heading: 'Por que os pagamentos mínimos custam anos da sua liberdade financeira',
    body: 'Os juros do cartão de crédito se acumulam mensalmente sobre o saldo devedor. Pagando apenas o mínimo, uma dívida média pode levar mais de 8–10 anos para ser quitada. Adicionar mesmo 10–15% a mais ao pagamento mínimo pode reduzir esse prazo pela metade e economizar milhares em juros. Use a calculadora de dívidas para calcular o tempo para pagar dívidas e ver o impacto exato de cada real extra que você destinar.',
  },
];

export default function CalculadoraDeDividas() {
  const [locale, setLocale] = useState('pt-br');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(PORTUGUESE_LOCALES.includes(detected) ? detected : 'pt-br');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/calculadora-de-dividas/"
      helmet={{
        lang: 'pt',
        title: 'Calculadora de Dívidas Grátis — Como Pagar Dívidas Rápido | ClearMyDebt',
        description: 'Calcule quando ficará livre de dívidas. Método bola de neve e avalanche. Sem cadastro. Resultados instantâneos.',
        ogTitle: 'Calculadora de Dívidas Grátis — Como Pagar Dívidas Rápido | ClearMyDebt',
        ogDescription: 'Calcule quando ficará livre de dívidas. Método bola de neve e avalanche. Sem cadastro. Resultados instantâneos.',
      }}
      hreflangList={[
        { hreflang: 'pt-br', href: 'https://clearmydebt.app/calculadora-de-dividas/' },
        { hreflang: 'pt-pt', href: 'https://clearmydebt.app/calculadora-de-dividas/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/calculadora-de-dividas/' },
      ]}
      h1="Calculadora de Dívidas Grátis"
      intro="Insira suas dívidas e descubra exatamente quando ficará livre de dívidas — e quanto dinheiro você economizará com o plano certo para pagar dívidas."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Perguntas Frequentes"
      moreHeading="Mais calculadoras"
      moreLinks={[
        { href: '/pt-br/credit-card-payoff-calculator/', label: 'Calculadora cartão de crédito' },
        { href: '/pt-br/debt-snowball-calculator/', label: 'Método bola de neve' },
        { href: '/pt-br/debt-avalanche-calculator/', label: 'Método avalanche' },
        { href: '/pt-br/debt-repayment-calculator/', label: 'Calculadora de pagamento' },
      ]}
    />
  );
}
